import { Heart, Calendar } from 'lucide-react';
import { timeline } from '../data/memories';

export const AboutPage = () => {
  return (
    <div className="min-h-screen pt-20 bg-[#0F0505]" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#722F37] rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#1B3A5F] rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center relative z-10">
          {/* Decorative Element */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-16 h-px bg-[#D4AF37]" />
            <Heart className="w-6 h-6 text-[#D4AF37]" fill="#D4AF37" />
            <div className="w-16 h-px bg-[#D4AF37]" />
          </div>

          <p className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">About Us</p>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-[#F5F5F0] mb-8">
            Our Story
          </h1>
          <p className="text-[#A18E90] text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
            A love story written in moments, sealed with promises, and celebrated every single day.
          </p>
        </div>
      </section>

      {/* How We Met Section */}
      <section className="py-16 md:py-24 bg-[#1A0F0F]/50" data-testid="how-we-met-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">Chapter One</p>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F5F5F0] mb-6">
                How We Met
              </h2>
              <div className="w-16 h-0.5 bg-[#D4AF37] mb-6" />
              <p className="text-[#A18E90] leading-relaxed mb-4">
                Some stories begin with grand gestures. Ours began with a simple conversation that felt anything but ordinary.
              </p>
              <p className="text-[#A18E90] leading-relaxed">
                From that first moment, something felt different. There was an ease, a comfort, a feeling that this was the beginning of something extraordinary. And it was.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-xl overflow-hidden border border-[#D4AF37]/20">
                <img
                  src="/images/about/how-we-met.jpeg"
                  alt="How we met"
                  className="w-full h-full object-cover img-hover"
                />
              </div>
              {/* Decorative frame */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-[#D4AF37]/30 rounded-xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* The Proposal Section */}
      <section className="py-16 md:py-24 bg-[#0F0505]" data-testid="proposal-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 relative">
              <div className="aspect-[4/5] rounded-xl overflow-hidden border border-[#D4AF37]/20">
                <img
                  src="/images/about/the-proposal.jpeg"
                  alt="The proposal"
                  className="w-full h-full object-cover img-hover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-full h-full border-2 border-[#D4AF37]/30 rounded-xl -z-10" />
            </div>
            <div className="order-1 md:order-2">
              <p className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">Chapter Two</p>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F5F5F0] mb-6">
                The Proposal
              </h2>
              <div className="w-16 h-0.5 bg-[#D4AF37] mb-6" />
              <p className="text-[#A18E90] leading-relaxed mb-4">
                I knew from early on that you were the one. Planning the proposal was the easiest and most nerve-wracking thing I've ever done.
              </p>
              <p className="text-[#A18E90] leading-relaxed">
                When you said yes, the world stopped for just a moment. In that second, I saw our entire future flash before my eyes — and it was beautiful.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why I Still Choose You Section */}
      <section className="py-16 md:py-24 bg-[#1A0F0F]/50" data-testid="why-i-choose-you-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <Heart className="w-10 h-10 text-[#D4AF37] mx-auto mb-8" fill="#D4AF37" />
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F5F5F0] mb-8">
            Why I Still Choose You
          </h2>
          <div className="w-16 h-0.5 bg-[#D4AF37] mx-auto mb-8" />
          <p className="font-['Cormorant_Garamond'] text-xl md:text-2xl text-[#A18E90] italic leading-relaxed mb-6">
            Every day this year has reminded me that marrying you was the best decision I have ever made.
          </p>
          <p className="text-[#A18E90] leading-relaxed mb-6">
            In your eyes, I find my home. In your laughter, I find my joy. In your arms, I find my peace. You make every ordinary moment feel like magic, and every challenge feel conquerable.
          </p>
          <p className="text-[#A18E90] leading-relaxed">
            I choose you today, tomorrow, and for all the days after that. Not because I have to, but because I can't imagine my life any other way.
          </p>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 md:py-24 bg-[#0F0505]" data-testid="timeline-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">Our Journey</p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F5F5F0]">
              The Timeline of Us
            </h2>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Center Line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#D4AF37] to-[#722F37]" />

            {timeline.map((item, index) => (
              <div
                key={index}
                className={`relative flex flex-col md:flex-row items-center mb-12 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
                data-testid={`timeline-item-${index}`}
              >
                {/* Content */}
                <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                  <div className="glass p-6 rounded-xl card-glow">
                    <span className="text-[#D4AF37] text-sm tracking-widest">{item.year}</span>
                    <h3 className="font-['Playfair_Display'] text-xl text-[#F5F5F0] mt-2 mb-3">
                      {item.title}
                    </h3>
                    <p className="text-[#A18E90] text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>

                {/* Center Dot */}
                <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#D4AF37] border-4 border-[#0F0505] z-10 shadow-[0_0_20px_rgba(212,175,55,0.5)]" />

                {/* Mobile Timeline Dot */}
                <div className="md:hidden flex items-center gap-4 my-4">
                  <div className="w-3 h-3 rounded-full bg-[#D4AF37]" />
                  <div className="flex-1 h-px bg-[#D4AF37]/30" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Quote */}
      <section className="py-20 md:py-32 bg-[#1A0F0F]/50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <Calendar className="w-8 h-8 text-[#D4AF37] mx-auto mb-8" />
          <blockquote className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-[#A18E90] italic leading-relaxed mb-8">
            "And so begins the next chapter of our story, with you by my side, forever and always."
          </blockquote>
          <div className="w-16 h-px bg-[#D4AF37] mx-auto" />
        </div>
      </section>
    </div>
  );
};
